import React, { ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Text as Typography } from './Text';
import { useAuth } from '../../hooks/useAuth';
import { useSystemConfigStore } from '../../stores/useSystemConfigStore';
import { useUIStateStore } from '../../stores/useUIStateStore';
import { useLabels } from '../../hooks/useLabels';
import Header from './Header';
import Sidebar from './Sidebar';
import SkipLink from './SkipLink';
import { ROLE_KEYS } from '../../constants/roleKeys';
import { useLayoutShell, LayoutShellProvider } from '../../contexts/LayoutShellContext';

interface PageContainerProps {
  children: ReactNode;
  pageTitle: string;
}

/**
 * MaintenancePage Component
 * Shown when the system is in maintenance mode.
 */
const MaintenancePage: React.FC<{ message: string }> = ({ message }) => {
  const { getLabel } = useLabels();
  return (
    <div className="bg-theme-partition w-full h-screen flex flex-col justify-center items-center text-center p-6">
      <div className="max-w-xl">
        <Typography variant="h1" className="text-4xl text-primary-600 dark:text-primary-400 mb-4">
          {getLabel('maintenance_title')}
        </Typography>
        <Typography variant="body-lg" className="text-neutral-700 dark:text-dark-textSecondary">
          {message || getLabel('maintenance_default_message')}
        </Typography>
      </div>
    </div>
  );
};

/**
 * PageContainer Component
 * 
 * DESIGN RATIONALE:
 * - Root layout driver for all authenticated pages.
 * - Uses CSS Grid (auto_1fr) to ensure the content area expands dynamically.
 * - NEW: Synchronized with global UIStateStore to prevent sidebar/content misalignment.
 * - 100% Stateless: Consumes state from stores and props.
 */
const PageContainer: React.FC<PageContainerProps> = ({ children, pageTitle }) => {
  const { user } = useAuth();
  const maintenance = useSystemConfigStore((state) => state.maintenance);
  const {
    isDesktopSidebarCollapsed,
    isSidebarHovered,
    isMobileSidebarOpen,
    setMobileSidebarOpen,
    setSidebarHovered,
    sidebarWidth, // Global width source
    isResizing, // Global resize state
  } = useUIStateStore();

  const location = useLocation();
  const { inShell } = useLayoutShell();
  const setActivePageTitle = useUIStateStore(s => s.setActivePageTitle);

  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname, setMobileSidebarOpen]);

  /**
   * Title Sync:
   * Polymorphic behavior - if we are inside a persistent shell, 
   * we just sync our prop to the global store so the root Header reflects it.
   */
  useEffect(() => {
    if (pageTitle) {
      setActivePageTitle(pageTitle);
    }
  }, [pageTitle, setActivePageTitle]);

  if (maintenance.maintenanceModeEnabled && user?.role !== ROLE_KEYS.SUPER_ADMIN) {
    return <MaintenancePage message={maintenance.maintenanceMessage || ''} />;
  }

  // If already in a shell, we just render children to avoid layout nesting duplicate sidebars
  if (inShell) {
    return <>{children}</>;
  }

  /**
   * isSidebarExpanded Calculation
   * MUST be identical to Sidebar.tsx logic to ensure grid synchronization.
   */
  const isSidebarExpanded = !isDesktopSidebarCollapsed || isSidebarHovered;

  return (
    <div className="bg-theme-bg-primary lg:grid lg:grid-cols-[auto_1fr] min-h-screen text-theme-text-primary">
      <SkipLink />
      {/* Mobile Sidebar (Overlay) \u2014 responsive width adapts to device screen */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        {/* Backdrop \u2014 blur for premium depth separation */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => setMobileSidebarOpen(false)}
        ></div>
        {/* Sidebar panel \u2014 responsive: 80% viewport width, capped at 280px */}
        <aside className="relative h-full w-[80vw] max-w-[280px]">
          <Sidebar />
        </aside>
      </div>

      {/* Desktop Sidebar (Grid Item) — SINGLE WIDTH AUTHORITY
         * Only this element transitions width. Sidebar.tsx fills with w-full.
         * Using transition-[width] instead of transition-all to prevent
         * layout thrashing from unrelated property transitions.
         * will-change hints the compositor for GPU-accelerated frames.
         */}
      <aside
        className={`hidden lg:block sticky top-0 h-screen overflow-hidden will-change-[width] ${isResizing ? 'transition-none' : 'transition-[width] duration-[var(--ui-animation-speed)] ease-[cubic-bezier(0.22,1,0.36,1)]'}`}
        style={{
          width: isSidebarExpanded ? `${sidebarWidth}px` : '80px'
        }}
        onMouseEnter={() => isDesktopSidebarCollapsed && setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
      >
        <Sidebar />
      </aside>

      {/* Main content (Grid Item) - Automatically expands when sidebar transitions */}
      <div className="flex flex-col min-w-0 bg-theme-partition">
        <Header pageTitle={pageTitle} />
        <main id="main-content" className="flex-1 overflow-y-auto p-4 md:p-6" tabIndex={-1}>
          {/* We wrap the child area in a ShellProvider so any nested PageContainers know they are "in shell" */}
          <LayoutShellProvider inShell={true}>
            {children}
          </LayoutShellProvider>
        </main>
      </div>
    </div>
  );
};

export default PageContainer;
